"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslations } from "next-intl";
import {
  CheckCircle,
  XCircle,
  Warning,
  Info,
  X,
} from "@phosphor-icons/react/dist/ssr";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ToastVariant = "success" | "error" | "warning" | "info";

export interface ToastItem {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number; // ms, default 4000
}

interface ToastContextValue {
  toast: (message: string, variant?: ToastVariant, duration?: number) => void;
  success: (message: string, duration?: number) => void;
  error:   (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
  info:    (message: string, duration?: number) => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

// ─── Individual toast ─────────────────────────────────────────────────────────

const ICONS: Record<ToastVariant, React.ReactNode> = {
  success: <CheckCircle size={18} weight="fill" aria-hidden="true" className="text-green-strong" />,
  error:   <XCircle    size={18} weight="fill" aria-hidden="true" className="text-[#DC2626]" />,
  warning: <Warning    size={18} weight="fill" aria-hidden="true" className="text-[#D97706]" />,
  info:    <Info       size={18} weight="fill" aria-hidden="true" className="text-navy" />,
};

const BORDER: Record<ToastVariant, string> = {
  success: "border-l-[3px] border-l-green-strong",
  error:   "border-l-[3px] border-l-[#DC2626]",
  warning: "border-l-[3px] border-l-[#D97706]",
  info:    "border-l-[3px] border-l-navy",
};

function ToastCard({
  item,
  onRemove,
}: {
  item: ToastItem;
  onRemove: (id: string) => void;
}) {
  const t = useTranslations("a11y");
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Slide in
  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Auto-dismiss
  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onRemove(item.id), 300);
    }, item.duration ?? 4000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [item.id, item.duration, onRemove]);

  function dismiss() {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(false);
    setTimeout(() => onRemove(item.id), 300);
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={[
        // items-center vertically aligns the icon, text, and close button
        // against the row's natural center — the 18px status icon and the
        // 14px close icon both look centered against single-line text
        // without per-element nudges. For multi-line messages the icons
        // float at vertical center of the wrapped text, which reads fine
        // for status indicators (they aren't bullets).
        "flex w-full max-w-sm items-center gap-13 rounded-card border border-navy/10 bg-white px-13 py-13 shadow-e4 transition-all duration-300",
        BORDER[item.variant],
        visible ? "translate-x-0 opacity-100" : "translate-x-6 opacity-0",
      ].join(" ")}
    >
      <span className="shrink-0">{ICONS[item.variant]}</span>
      <p className="flex-1 text-sm leading-snug text-navy">{item.message}</p>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t("dismissNotification")}
        className="shrink-0 text-gray-500 hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const t = useTranslations("a11y");
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const remove = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, variant: ToastVariant = "info", duration?: number) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setToasts(prev => [...prev, { id, message, variant, duration }]);
    },
    [],
  );

  const success = useCallback((m: string, d?: number) => toast(m, "success", d), [toast]);
  const error   = useCallback((m: string, d?: number) => toast(m, "error",   d), [toast]);
  const warning = useCallback((m: string, d?: number) => toast(m, "warning", d), [toast]);
  const info    = useCallback((m: string, d?: number) => toast(m, "info",    d), [toast]);

  return (
    <ToastContext.Provider value={{ toast, success, error, warning, info }}>
      {children}

      {/* Portal — fixed top-right stack.
          `role="region"` because a bare <div> has the implicit `generic`
          role, on which ARIA prohibits `aria-label` (no anchor to attach
          the accessible name to). `region` is a landmark role that
          REQUIRES an accessible name, so `aria-label="Notifications"`
          is now legal and surfaces a proper landmark for AT users.
          Individual toasts inside still carry role="status" / aria-live
          so each new entry is announced as it arrives. */}
      <div
        role="region"
        aria-label={t("notifications")}
        className="pointer-events-none fixed right-21 top-21 z-nav flex flex-col items-end gap-8"
      >
        {toasts.map(item => (
          <div key={item.id} className="pointer-events-auto w-full max-w-sm">
            <ToastCard item={item} onRemove={remove} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
